// src/pages/ProjectDetail.js
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import {
  doc,
  onSnapshot,
  getDocs,
  collection,
  query,
  where,
} from "firebase/firestore";
import jsPDF from "jspdf";
import {
  ChevronLeft,
  Download,
  Building2,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";

/* ============================== Helpers ============================== */
const clsx = (...xs) => xs.filter(Boolean).join(" ");
const pct = (n) => Math.max(0, Math.min(100, Math.round(Number(n || 0))));
const PRE_CODES = ["MOU", "SURVEY", "SOIL", "DESIGN", "TENDER", "WORK"];

const STAGE_NAMES = {
  MOU: "MOU",
  SURVEY: "Site Survey",
  SOIL: "Soil Testing",
  DESIGN: "Design & Structure",
  TENDER: "Tender Process",
  WORK: "Work Started",
  EXCAVATION: "Excavation / Site Work",
  FOOTING: "Footing",
  PLINTH: "Plinth Beam",
  COLUMN_BEAM: "Column & Beam Work",
  BRICK: "Brick Masonry Work",
  PLASTER: "Plaster Work",
  PEB_INSPECTION: "PEB Inspection",
  PEB_INSTALL: "PEB Installation",
  COLOUR: "Colour Work",
  LOGO: "Logo Design & Branding",
  FINISH: "Finishing",
  DONE: "Completed",
};
const pretty = (code) => STAGE_NAMES[code] || code;

function formatDate(ts) {
  try {
    if (!ts) return "—";
    if (typeof ts.toDate === "function")
      return ts.toDate().toLocaleDateString();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
  } catch {
    return "—";
  }
}

function statusIcon(status = "Pending") {
  const s = String(status || "Pending").toLowerCase();
  if (s === "completed")
    return <CheckCircle className="w-4 h-4 text-emerald-600" />;
  if (s === "rejected") return <XCircle className="w-4 h-4 text-rose-600" />;
  if (s.includes("progress")) return <Clock className="w-4 h-4 text-amber-600" />;
  return <Clock className="w-4 h-4 text-slate-400" />;
}

/* ============================== Stage Card (UI) ============================== */
function StageCard({ s }) {
  const v = String(s.status || "Pending").toLowerCase();
  const tone =
    v === "completed"
      ? "border-emerald-200 bg-emerald-50"
      : v === "rejected"
      ? "border-rose-200 bg-rose-50"
      : v.includes("progress")
      ? "border-amber-200 bg-amber-50"
      : "border-slate-200 bg-white";

  const stamp =
    s.completedAt || s.startedAt || s.lastStageUpdatedAt || s.rejectedAt;

  return (
    <div
      className={clsx(
        "rounded-xl border shadow-sm p-3 flex items-start justify-between gap-3 transition hover:shadow-md",
        tone
      )}
    >
      <div className="flex items-start gap-3">
        {statusIcon(s.status)}
        <div>
          <div className="font-semibold text-sm text-slate-900">
            {s.name || pretty(s.code)}
          </div>
          <div className="text-xs text-slate-600">
            {v === "completed"
              ? "Completed"
              : v === "rejected"
              ? "Rejected"
              : v.includes("progress")
              ? "In Progress"
              : "Pending"}{" "}
            • {formatDate(stamp)}
          </div>
          {s.lastStageNote ? (
            <div className="text-[11px] text-slate-500 mt-1 italic">
              “{s.lastStageNote}”
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ============================== Letterhead loader ============================== */
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
async function renderLetterhead(pdf) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const candidates = [
    "/letterheads/saam-letterhead.png",
    "/letterheads/saam-letterhead.jpg",
    "/letterheads/saam-letterhead.jpeg",
  ];
  for (const url of candidates) {
    try {
      const img = await loadImage(url);
      const ext = url.split(".").pop().toLowerCase();
      const fmt = ext === "png" ? "PNG" : "JPEG";
      pdf.addImage(img, fmt, 0, 0, W, H);
      return;
    } catch {}
  }
}

/* ============================== PDF utils ============================== */
const HEX_BLACK = "#000000";
const HEX_HEAD = "#0f172a";
const HEX_ACC = "#1d4ed8";
const ROW_H = 20;

function rightText(pdf, txt, xRight, y) {
  const w = pdf.getTextWidth(String(txt || ""));
  pdf.text(String(txt || ""), xRight - w, y);
}

/* ===== super black-line table (paged, repeating header, safe wrap) ===== */
function drawBlackTablePaged(pdf, x, y, colW, headers, rows, ctx) {
  const { H, TM, BM, onNewPage, headerTitle } = ctx;
  const padX = 6;
  const totalW = colW.reduce((a, b) => a + b, 0);

  const ensureSpace = (need, redrawHeader = false) => {
    if (y + need <= H - BM) return;
    pdf.addPage();
    y = onNewPage ? onNewPage() : TM;
    if (headerTitle) {
      pdf.setFont("Helvetica", "normal");
      pdf.setFontSize(13);
      pdf.setTextColor(HEX_HEAD);
      pdf.text(headerTitle + " (cont.)", x, y - 6);
    }
    if (redrawHeader) drawHeaderRow();
  };

  const drawHeaderRow = () => {
    pdf.setDrawColor(HEX_BLACK);
    pdf.setFillColor(255, 255, 255);
    pdf.rect(x, y, totalW, 24, "F");
    pdf.rect(x, y, totalW, 24, "S");
    let cx = x;
    pdf.setFont("Helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(HEX_HEAD);
    headers.forEach((h, i) => {
      pdf.text(String(h), cx + padX, y + 16);
      cx += colW[i];
      if (i < headers.length - 1) pdf.line(cx, y, cx, y + 24);
    });
    pdf.line(x, y + 24, x + totalW, y + 24);
    y += 24;
  };

  // header
  ensureSpace(24);
  drawHeaderRow();

  // rows
  rows.forEach((row, idx) => {
    const colLines = row.map((cell, i) =>
      pdf.splitTextToSize(String(cell ?? "—"), colW[i] - padX * 2)
    );
    const h = Math.max(ROW_H, ...colLines.map((lines) => lines.length * 12 + 8));

    ensureSpace(h, true);

    // zebra fill
    if (idx % 2 === 1) {
      pdf.setFillColor(250, 250, 250);
      pdf.rect(x, y, totalW, h, "F");
    }

    // outer + verticals
    pdf.setDrawColor(HEX_BLACK);
    pdf.rect(x, y, totalW, h, "S");
    let vx = x;
    for (let i = 0; i < colW.length - 1; i++) {
      vx += colW[i];
      pdf.line(vx, y, vx, y + h);
    }

    // text
    let tx = x;
    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor("#111111");
    colLines.forEach((lines, i) => {
      lines.forEach((ln, li) => pdf.text(ln, tx + padX, y + 12 + li * 12));
      tx += colW[i];
    });

    pdf.line(x, y + h, x + totalW, y + h);
    y += h;
  });

  return y;
}

/* ========== mappers for tables (KEY DETAILS + MILESTONES) ========== */
function detailsRows(p) {
  const title = p.pacsName || p.name || "—";
  return [
    ["Project", title, "Project ID", p.projectId || p.id || "—"],
    ["District", p.district || "—", "Owner", p.owner || "—"],
    [
      "Status",
      p.currentStage || "—",
      "Progress",
      `${pct(p.progressPercent)}%`,
    ],
    [
      "Created",
      p.createdAt ? formatDate(p.createdAt) : "—",
      "Updated",
      p.lastUpdated ? formatDate(p.lastUpdated) : "—",
    ],
  ].map(([k1, v1, k2, v2]) => [k1, v1, k2, v2]);
}
function milestoneRows(list) {
  return list.map((s) => {
    const raw = String(s.status || "Pending").toLowerCase();
    const st =
      raw === "in progress"
        ? "In Progress"
        : raw.charAt(0).toUpperCase() + raw.slice(1);
    const when =
      s.completedAt?.toDate?.() ||
      s.startedAt?.toDate?.() ||
      s.lastStageUpdatedAt?.toDate?.() ||
      null;
    const dStr = when ? new Date(when).toLocaleDateString() : "—";
    return [
      s.name || STAGE_NAMES[s.code] || s.code || "—",
      st,
      dStr,
      s.lastStageNote || "—",
    ];
  });
}

/* ============================== PDF Builder (detailed) ============================== */
async function downloadProjectPDFPro(project) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const LM = 64,
    RM = 64,
    TM = 76,
    BM = 60;

  await renderLetterhead(pdf);

  // Title
  let y = 150;
  pdf.setFont("Helvetica", "normal");
  pdf.setFontSize(22);
  pdf.setTextColor(HEX_HEAD);
  pdf.text("PROJECT SUMMARY", LM, y);
  pdf.setDrawColor(HEX_ACC);
  pdf.setLineWidth(1.4);
  pdf.line(LM, y + 10, W - RM, y + 10);
  y += 28;

  // Top capsule
  const capX = LM,
    capW = W - LM - RM;
  const title = (project.pacsName || project.name || "PROJECT").toUpperCase();
  const pid = project.projectId || project.id || "—";
  const district = project.district || "—";
  const owner = project.owner || "—";
  const status = project.currentStage || "—";
  const progress = `${pct(project.progressPercent || 0)}%`;
  const created = project.createdAt ? formatDate(project.createdAt) : "—";
  const updated = project.lastUpdated ? formatDate(project.lastUpdated) : "—";

  const titleLines = pdf.splitTextToSize(
    `${title}${pid !== "—" ? `  (${pid})` : ""}`,
    capW - 16
  );
  const hTitle = Math.max(18, titleLines.length * 12 + 8);
  const capH = hTitle + 16 * 3 + 16;

  pdf.setFillColor(255, 255, 255);
  pdf.rect(capX, y, capW, capH, "F");
  pdf.setDrawColor("#1e293b");
  pdf.rect(capX, y, capW, capH, "S");

  let cy = y + 16;
  pdf.setFont("Helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(HEX_HEAD);
  pdf.text(titleLines, capX + 8, cy);
  cy += hTitle - 6;
  pdf.setFont("Helvetica", "normal");
  pdf.setFontSize(10.5);
  pdf.setTextColor("#111111");
  pdf.text(`District: ${district}   |   Owner: ${owner}`, capX + 8, (cy += 16));
  pdf.text(
    `Status: ${status}    |   Progress: ${progress}`,
    capX + 8,
    (cy += 16)
  );
  pdf.text(
    `Created: ${created}  |  Updated: ${updated}`,
    capX + 8,
    (cy += 16)
  );

  y += capH + 22;

  const ctxBase = {
    H,
    TM,
    BM,
    onNewPage: () => 100,
  };

  // KEY DETAILS
  pdf.setFont("Helvetica", "normal");
  pdf.setFontSize(13);
  pdf.setTextColor(HEX_HEAD);
  pdf.text("KEY DETAILS", LM, y);
  y = drawBlackTablePaged(
    pdf,
    LM,
    y + 10,
    [
      (W - LM - RM) * 0.18,
      (W - LM - RM) * 0.32,
      (W - LM - RM) * 0.18,
      (W - LM - RM) * 0.32,
    ],
    ["Field", "Value", "Field", "Value"],
    detailsRows(project),
    { ...ctxBase, headerTitle: "KEY DETAILS" }
  );
  y += 18;

  const stages = Array.isArray(project.stages) ? project.stages : [];
  const pre = stages.filter((s) => PRE_CODES.includes(s.code));
  const post = stages.filter((s) => !PRE_CODES.includes(s.code));

  const mhdrs = ["Milestone", "Status", "Date", "Note"];
  const mcols = [
    (W - LM - RM) * 0.34,
    (W - LM - RM) * 0.16,
    (W - LM - RM) * 0.16,
    (W - LM - RM) * 0.34,
  ];

  if (pre.length) {
    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(13);
    pdf.setTextColor(HEX_HEAD);
    pdf.text("PRE-WORK MILESTONES", LM, y);
    y = drawBlackTablePaged(
      pdf,
      LM,
      y + 10,
      mcols,
      mhdrs,
      milestoneRows(pre),
      { ...ctxBase, headerTitle: "PRE-WORK MILESTONES" }
    );
    y += 16;
  }

  if (post.length) {
    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(13);
    pdf.setTextColor(HEX_HEAD);
    pdf.text("POST-WORK MILESTONES", LM, y);
    y = drawBlackTablePaged(
      pdf,
      LM,
      y + 10,
      mcols,
      mhdrs,
      milestoneRows(post),
      { ...ctxBase, headerTitle: "POST-WORK MILESTONES" }
    );
  }

  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p);
    pdf.setDrawColor(HEX_HEAD);
    pdf.setFillColor(15, 23, 42);
    pdf.rect(0, H - 34, W, 34, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text("www.shineagri.org", 64, H - 14);
    pdf.text("saamcopsociety@gmail.com", W / 2 - 60, H - 14);
    rightText(pdf, "+91 84908 84020", W - 64, H - 14);

    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(200);
    rightText(pdf, `Page ${p} of ${pages}`, W - 64, H - 44);
  }

  pdf.save(
    `Project_${(project.pacsName || project.name || "summary")
      .replace(/[^a-z0-9]/gi, "_")
      .toUpperCase()}.pdf`
  );
}

/* ======================= CSV – Stage register (for sharing) ======================= */
function downloadStageCSV(project, stages) {
  if (!stages?.length) {
    alert("No stages available for this project.");
    return;
  }
  const header = [
    "Stage Code",
    "Stage Name",
    "Status",
    "Date",
    "Note",
  ].join(",");

  const rows = stages.map((s) => {
    const raw = String(s.status || "Pending").toLowerCase();
    const st =
      raw === "in progress"
        ? "In Progress"
        : raw.charAt(0).toUpperCase() + raw.slice(1);
    const when =
      s.completedAt?.toDate?.() ||
      s.startedAt?.toDate?.() ||
      s.lastStageUpdatedAt?.toDate?.() ||
      null;
    const dStr = when ? new Date(when).toLocaleDateString() : "";
    const cells = [
      s.code || "",
      (s.name || STAGE_NAMES[s.code] || "").replace(/,/g, " "),
      st.replace(/,/g, " "),
      dStr,
      (s.lastStageNote || "").replace(/,/g, " "),
    ];
    return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
  });

  const content = [header, ...rows].join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const nameSafe =
    (project.pacsName || project.name || "project").replace(
      /[^a-z0-9]/gi,
      "_"
    ) || "project";
  a.href = url;
  a.download = `${nameSafe}_stages.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

/* ============================== Subscribe hook ============================== */
function useProjectSubscribe(projectParamId) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectParamId) return;
    let unsub = null;

    const ref = doc(db, "projects", projectParamId);
    unsub = onSnapshot(
      ref,
      async (snap) => {
        if (snap.exists()) {
          setProject({ id: snap.id, ...snap.data() });
          setLoading(false);
        } else {
          try {
            const qRef = query(
              collection(db, "projects"),
              where("projectId", "==", projectParamId)
            );
            const res = await getDocs(qRef);
            if (!res.empty) {
              const found = res.docs[0];
              if (unsub) unsub();
              const ref2 = doc(db, "projects", found.id);
              unsub = onSnapshot(ref2, (s2) => {
                setProject({ id: s2.id, ...s2.data() });
                setLoading(false);
              });
            } else {
              setProject(null);
              setLoading(false);
            }
          } catch (e) {
            console.error("projectId lookup error:", e);
            setProject(null);
            setLoading(false);
          }
        }
      },
      (err) => {
        console.error("project subscribe error:", err);
        setProject(null);
        setLoading(false);
      }
    );

    return () => {
      if (unsub) unsub();
    };
  }, [projectParamId]);

  return { project, loading };
}

/* ============================== PAGE – PREMIUM DASHBOARD VIEW ============================== */
export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { project, loading } = useProjectSubscribe(id);

  const stages = Array.isArray(project?.stages) ? project.stages : [];
  const preStages = useMemo(
    () => stages.filter((s) => PRE_CODES.includes(s.code)),
    [stages]
  );
  const postStages = useMemo(
    () => stages.filter((s) => !PRE_CODES.includes(s.code)),
    [stages]
  );

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center text-slate-600">
        Loading project…
      </div>
    );
  if (!project)
    return (
      <div className="min-h-screen grid place-items-center text-slate-600">
        Project not found.
      </div>
    );

  const progress = pct(project.progressPercent);
  const doneCount = stages.filter(
    (s) => String(s.status || "").toLowerCase() === "completed"
  ).length;
  const total = stages.length;
  const pending = Math.max(0, total - doneCount);
  const preDone = preStages.filter(
    (s) => String(s.status || "").toLowerCase() === "completed"
  ).length;
  const postDone = postStages.filter(
    (s) => String(s.status || "").toLowerCase() === "completed"
  ).length;

  // derive some optional fields for snapshot
  const capacity = project.capacityMT || project.capacity || project.mtCapacity;
  const scheme = project.schemeName || project.scheme || project.schemeCode;
  const bank = project.bankName || project.nodalAgency || project.financier;
  const taluka = project.taluka || project.block;
  const village = project.village || project.pacsVillage;
  const cost = project.projectCost || project.estimatedCost;

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-5">
      <div className="max-w-[1200px] mx-auto space-y-5">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Projects
          </button>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => downloadStageCSV(project, stages)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs sm:text-sm text-slate-800 hover:bg-slate-50 shadow-sm"
            >
              <Download className="w-4 h-4" />
              Stage Register (CSV)
            </button>
            <button
              onClick={() => downloadProjectPDFPro(project)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-emerald-500 text-white rounded-lg shadow-sm hover:brightness-95 text-xs sm:text-sm"
            >
              <Download className="w-4 h-4" />
              Detailed Report (PDF)
            </button>
          </div>
        </div>

        {/* HEADER CARD – project capsule */}
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
          <div className="flex flex-wrap justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {project.pacsName || project.name}
              </h1>
              <div className="mt-1 text-xs text-slate-600 space-x-1">
                <span>ID: {project.projectId || project.id}</span>
                <span>•</span>
                <span>{project.district || "District —"}</span>
                {taluka && (
                  <>
                    <span>•</span>
                    <span>{taluka}</span>
                  </>
                )}
                {village && (
                  <>
                    <span>•</span>
                    <span>{village}</span>
                  </>
                )}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Owner: {project.owner || "—"}
              </div>
            </div>

            <div className="text-right space-y-1">
              <div className="text-sm font-semibold text-slate-900">
                Overall Progress: {progress}%
              </div>
              <div className="w-40 bg-slate-200 h-2 rounded-full overflow-hidden ml-auto">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="text-[11px] text-slate-500">
                Last updated: {formatDate(project.lastUpdated)}
              </div>
            </div>
          </div>

          {/* chips */}
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">
              {project.currentStage || "Current Stage —"}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
              Total stages: {total || 0}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Completed: {doneCount}
            </span>
            {capacity && (
              <span className="px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700">
                Capacity: {capacity} MT
              </span>
            )}
            {scheme && (
              <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">
                Scheme: {scheme}
              </span>
            )}
          </div>
        </section>

        {/* METRIC GRID – small dashboard on top */}
        <section className="grid md:grid-cols-4 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Overall Progress
            </div>
            <div className="text-2xl font-bold text-slate-900">{progress}%</div>
            <div className="text-[11px] text-slate-500">
              {doneCount} stages completed out of {total}.
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Pre-Work
            </div>
            <div className="text-xl font-semibold text-blue-700">
              {preDone}/{preStages.length || 0}
            </div>
            <div className="text-[11px] text-slate-500">
              Survey, soil, design, tender etc.
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Construction
            </div>
            <div className="text-xl font-semibold text-emerald-700">
              {postDone}/{postStages.length || 0}
            </div>
            <div className="text-[11px] text-slate-500">
              On-site execution milestones.
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Pending Stages
            </div>
            <div className="text-xl font-semibold text-amber-700">
              {pending}
            </div>
            <div className="text-[11px] text-slate-500">
              Yet to be completed to finish the godown.
            </div>
          </div>
        </section>

        {/* TWO COLUMN LAYOUT – left timelines, right snapshot & contacts */}
        <section className="grid lg:grid-cols-3 gap-5">
          {/* LEFT: timelines (2 cols) */}
          <div className="lg:col-span-2 space-y-5">
            {/* Pre-work */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-slate-800">
                  Pre-Work Timeline
                </h2>
                <span className="text-[10px] text-slate-500">
                  MOU, survey, soil, design & tender.
                </span>
              </div>
              {preStages.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {preStages.map((s) => (
                    <StageCard key={s.code} s={s} />
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500">
                  No pre-work stages found.
                </div>
              )}
            </div>

            {/* Post-work */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-slate-800">
                  Construction Timeline
                </h2>
                <span className="text-[10px] text-slate-500">
                  Excavation to final finishing.
                </span>
              </div>
              {postStages.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {postStages.map((s) => (
                    <StageCard key={s.code} s={s} />
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500">
                  No construction stages found.
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: snapshot + contractor + extra info */}
          <div className="space-y-4">
            {/* snapshot */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">
                Project Snapshot
              </h3>
              <dl className="space-y-2 text-xs text-slate-700">
                <div className="flex justify-between gap-3">
                  <dt className="font-medium text-slate-600 w-24">District</dt>
                  <dd className="flex-1 text-right">{project.district || "—"}</dd>
                </div>
                {taluka && (
                  <div className="flex justify-between gap-3">
                    <dt className="font-medium text-slate-600 w-24">Taluka</dt>
                    <dd className="flex-1 text-right">{taluka}</dd>
                  </div>
                )}
                {village && (
                  <div className="flex justify-between gap-3">
                    <dt className="font-medium text-slate-600 w-24">Village</dt>
                    <dd className="flex-1 text-right">{village}</dd>
                  </div>
                )}
                {capacity && (
                  <div className="flex justify-between gap-3">
                    <dt className="font-medium text-slate-600 w-24">
                      Capacity
                    </dt>
                    <dd className="flex-1 text-right">{capacity} MT</dd>
                  </div>
                )}
                {cost && (
                  <div className="flex justify-between gap-3">
                    <dt className="font-medium text-slate-600 w-24">Cost</dt>
                    <dd className="flex-1 text-right">
                      ₹{" "}
                      {Number(cost || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </dd>
                  </div>
                )}
                {scheme && (
                  <div className="flex justify-between gap-3">
                    <dt className="font-medium text-slate-600 w-24">Scheme</dt>
                    <dd className="flex-1 text-right">{scheme}</dd>
                  </div>
                )}
                {bank && (
                  <div className="flex justify-between gap-3">
                    <dt className="font-medium text-slate-600 w-24">
                      Bank / Agency
                    </dt>
                    <dd className="flex-1 text-right">{bank}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <dt className="font-medium text-slate-600 w-24">Created</dt>
                  <dd className="flex-1 text-right">
                    {project.createdAt ? formatDate(project.createdAt) : "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-medium text-slate-600 w-24">Updated</dt>
                  <dd className="flex-1 text-right">
                    {project.lastUpdated ? formatDate(project.lastUpdated) : "—"}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Contractor */}
            <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">
                Assigned Contractor
              </h3>
              {project.contractorName ? (
                <div className="flex justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">
                        {project.contractorName}
                      </div>
                      <div className="text-xs text-slate-600">
                        {project.contractorCompany || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-700 text-right">
                    {project.contractorMobile && (
                      <div>📞 {project.contractorMobile}</div>
                    )}
                    {project.loaDate && (
                      <div>LOA: {formatDate(project.loaDate)}</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 text-amber-800 border border-amber-200 rounded-lg p-3 text-xs">
                  Contractor not assigned yet.
                </div>
              )}
            </div>

            {/* Optional links/documents if present */}
            {(project.tenderUrl ||
              project.soilReportUrl ||
              project.designUrl ||
              project.otherDocUrl) && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">
                  Documents / Links
                </h3>
                <ul className="space-y-1 text-xs text-blue-700">
                  {project.tenderUrl && (
                    <li>
                      <button
                        className="hover:underline"
                        onClick={() => window.open(project.tenderUrl, "_blank")}
                      >
                        Tender Document
                      </button>
                    </li>
                  )}
                  {project.soilReportUrl && (
                    <li>
                      <button
                        className="hover:underline"
                        onClick={() =>
                          window.open(project.soilReportUrl, "_blank")
                        }
                      >
                        Soil Report
                      </button>
                    </li>
                  )}
                  {project.designUrl && (
                    <li>
                      <button
                        className="hover:underline"
                        onClick={() =>
                          window.open(project.designUrl, "_blank")
                        }
                      >
                        Design Drawing
                      </button>
                    </li>
                  )}
                  {project.otherDocUrl && (
                    <li>
                      <button
                        className="hover:underline"
                        onClick={() =>
                          window.open(project.otherDocUrl, "_blank")
                        }
                      >
                        Other Document
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
